export interface IAppConfig {
  Version: string;
  EnableNotifications: boolean;
  DefaultLanguage: string;
  TimeZone: string;
  UseProfile: boolean;
  UseLogin: boolean;
  UseLocationServices: boolean;
}

export interface IAppTheme {
  ConfigVersion: string;
  PrimaryColor: string;
  PrimaryTextColor: string;
  SecondaryColor: string;
  SecondaryTextColor: string;
  BackgroundColor: string;
  BackgroundImageUrl: string;
  FontFamily: string;
  TextColor: string;
  SplashImageUrl: string;
}

export interface IAppScreen {
  Title: string;
  MenuIconUrl: string;
  MenuTitle: string;
  ShowInMenu: boolean;
  ShowInLowMenu: boolean;
  OrderInMenu: number;
  Type: string;
  //JsonContent: string;
  JsonContentDic: Record<string, string>;
  AccessRole: string;
  IsSecure: boolean;
  Id?: number;
  PublicId?: string;
  CreatedAtUtcDate?: string;
  SplashImageUseDefault?: boolean;
  SplashImageUrl?: string;
  BackgroundImageUseDefault?: boolean;
  BackgroundImageUrl?: string;
  // Fair-local date/time strings (datetime-local input format). Leave both blank to always display the screen.
  DisplayStartAtUtc?: string;
  DisplayEndAtUtc?: string;
}

// Screen Type Constants
export const ScreenTypes = {
  FILE_PAGE: 'FilePage',
  FAQ_PAGE: 'FAQPage',
  SIMPLE_TEXT_PAGE: 'SimpleTextPage',
  FORMATTED_TEXT_PAGE: 'FormattedTextPage',
  PARENT_SCREEN: 'ParentScreen',
  NOTIFICATION_SCREEN: 'NotificationScreen',
  WEBVIEW_SCREEN: 'WebViewScreen',
  WEBLINK_SCREEN: 'WebLinkScreen',
} as const;

export type ScreenType = (typeof ScreenTypes)[keyof typeof ScreenTypes];

// Content Interfaces for each screen type
export interface IFilePageContent {
  fileUrl: string;
  fileName: string;
  fileType: 'pdf' | 'image' | 'text' | 'video';
  description?: string;
  allowDownload: boolean;
}

export interface IFAQItem {
  id: string;
  question: string;
  answer: string;
  order: number;
}

export interface IFAQPageContent {
  title: string;
  description?: string;
  faqObj: IFAQItem[];
  faqs?: string;
  allowSearch: boolean;
  faqLength: number;
}

export interface ISimpleTextPageContent {
  title: string;
  content: string;
  textAlign: 'left' | 'center' | 'right' | 'justify';
}

export interface IFormattedTextPageContent {
  title: string;
  content: string; // HTML content from Editor
  showTableOfContents: boolean;
}

export interface IChildScreenReference {
  screenId: string; // Reference to existing screen's PublicId
  displayTitle?: string; // Override title for display in parent
  summary?: string; // Description (for List/Cards modes)
  iconUrl?: string; // Icon (for List/Cards modes)
  order: number; // Display order
}

export interface IParentScreenContent {
  title: string; // Parent screen title
  description?: string; // Optional parent screen description
  displayMode: 'List' | 'Dropdown' | 'Cards';
  childScreens_string?: string; // JSON string of IChildScreenReference[]
  childScreenLength?: number; // Number of child screens
  showSummary: string; // Show summary/description (List/Cards only)
  showIcons: string; // Show icons (List/Cards only)
  headerContent?: string; // Optional HTML content above child screens
}

export interface INotificationScreenContent {
  headerContent?: string;
  footerContent?: string;
}

export interface IWebViewScreenContent {
  url: string;
  title?: string;
  showNavigationControls?: boolean;
}

export interface IWebLinkScreenContent {
  url: string;
  title?: string;
  openInNewTab?: boolean;
}

// Union type for all content types
export type IAppScreenContent = IFilePageContent | IFAQPageContent | ISimpleTextPageContent | IFormattedTextPageContent | IParentScreenContent | INotificationScreenContent | IWebViewScreenContent | IWebLinkScreenContent;

// Helper function for URL validation
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Utility functions for JSON serialization
export const ScreenContentUtils = {
  ToDic: (content: IAppScreenContent): Record<string, string> => {
    let dic: Record<string, string> = {};
    let keys = Object.keys(content);
    keys.forEach((key) => {
      if (key === 'faqsObjs') {
        dic[key + '_string'] = JSON.stringify(content[key]);
      } else if (key === 'childScreens') {
        dic[key + '_string'] = JSON.stringify(content[key]);
      } else {
        dic[key] = content[key] + '';
      }
    });
    return dic;
  },

  deserialize: <T extends IAppScreenContent>(jsonContent: string): T | null => {
    try {
      return JSON.parse(jsonContent) as T;
    } catch {
      return null;
    }
  },

  getDefaultContent: (type: ScreenType): IAppScreenContent => {
    switch (type) {
      case ScreenTypes.FILE_PAGE:
        return {
          fileUrl: '',
          fileName: '',
          fileType: 'pdf',
          description: '',
          allowDownload: true,
        } as IFilePageContent;

      case ScreenTypes.FAQ_PAGE:
        return {
          title: 'Frequently Asked Questions',
          description: '',
          faqObj: [],
          allowSearch: true,
        } as IFAQPageContent;

      case ScreenTypes.SIMPLE_TEXT_PAGE:
        return {
          title: '',
          content: '',
          textAlign: 'left',
        } as ISimpleTextPageContent;

      case ScreenTypes.FORMATTED_TEXT_PAGE:
        return {
          title: '',
          content: '',
          showTableOfContents: false,
        } as IFormattedTextPageContent;

      case ScreenTypes.PARENT_SCREEN:
        return {
          title: '',
          description: '',
          displayMode: 'List',
          childScreens: [],
          showSummary: 'false',
          showIcons: 'false',
        } as IParentScreenContent;

      case ScreenTypes.WEBVIEW_SCREEN:
        return {
          url: '',
          title: '',
          showNavigationControls: true,
        } as IWebViewScreenContent;

      case ScreenTypes.WEBLINK_SCREEN:
        return {
          url: '',
          title: '',
          openInNewTab: false,
        } as IWebLinkScreenContent;

      default:
        return {
          title: '',
          content: '',
          textAlign: 'left',
        } as ISimpleTextPageContent;
    }
  },

  validateContent: (type: ScreenType, content: IAppScreenContent): boolean => {
    switch (type) {
      case ScreenTypes.FILE_PAGE:
        const fileContent = content as IFilePageContent;
        return !!(fileContent.fileUrl && fileContent.fileName);

      case ScreenTypes.FAQ_PAGE:
        const faqContent = content as IFAQPageContent;
        return !!(faqContent.title && faqContent.faqObj.length > 0 && faqContent.faqObj.every((faq) => faq.question.trim() && faq.answer.trim()));

      case ScreenTypes.SIMPLE_TEXT_PAGE:
        const simpleContent = content as ISimpleTextPageContent;
        return !!(simpleContent.title && simpleContent.content);

      case ScreenTypes.FORMATTED_TEXT_PAGE:
        const formattedContent = content as IFormattedTextPageContent;
        return !!(formattedContent.title && formattedContent.content);

      case ScreenTypes.PARENT_SCREEN:
        const parentContent = content as IParentScreenContent;
        return !!(parentContent.title && parentContent.displayMode);

      case ScreenTypes.WEBVIEW_SCREEN:
        const webViewContent = content as IWebViewScreenContent;
        return !!(webViewContent.url && webViewContent.url.trim());

      case ScreenTypes.WEBLINK_SCREEN:
        const webLinkContent = content as IWebLinkScreenContent;
        return !!(webLinkContent.url && webLinkContent.url.trim());

      default:
        return false;
    }
  },

  getValidationErrors: (type: ScreenType, content: IAppScreenContent): string[] => {
    const errors: string[] = [];

    switch (type) {
      case ScreenTypes.FILE_PAGE:
        const fileContent = content as IFilePageContent;
        if (!fileContent.fileUrl) errors.push('File must be uploaded');
        if (!fileContent.fileName) errors.push('File name is required');
        break;

      case ScreenTypes.FAQ_PAGE:
        const faqContent = content as IFAQPageContent;
        if (!faqContent.title) errors.push('Page title is required');
        if (faqContent.faqObj.length === 0) {
          errors.push('At least one FAQ item is required');
        } else {
          faqContent.faqObj.forEach((faq, index) => {
            if (!faq.question.trim()) errors.push(`FAQ ${index + 1}: Question is required`);
            if (!faq.answer.trim()) errors.push(`FAQ ${index + 1}: Answer is required`);
          });
        }
        break;

      case ScreenTypes.SIMPLE_TEXT_PAGE:
        const simpleContent = content as ISimpleTextPageContent;
        if (!simpleContent.title) errors.push('Page title is required');
        if (!simpleContent.content) errors.push('Content is required');
        break;

      case ScreenTypes.FORMATTED_TEXT_PAGE:
        const formattedContent = content as IFormattedTextPageContent;
        if (!formattedContent.title) errors.push('Page title is required');
        if (!formattedContent.content) errors.push('Content is required');
        break;

      case ScreenTypes.PARENT_SCREEN:
        const parentContent = content as IParentScreenContent;
        if (!parentContent.title) errors.push('Page title is required');
        if (!parentContent.displayMode) errors.push('Display mode is required');
        break;

      case ScreenTypes.WEBVIEW_SCREEN:
        const webViewContent = content as IWebViewScreenContent;
        if (!webViewContent.url || !webViewContent.url.trim()) {
          errors.push('URL is required');
        } else if (!isValidUrl(webViewContent.url)) {
          errors.push('Please enter a valid URL');
        }
        break;

      case ScreenTypes.WEBLINK_SCREEN:
        const webLinkContent = content as IWebLinkScreenContent;
        if (!webLinkContent.url || !webLinkContent.url.trim()) {
          errors.push('URL is required');
        } else if (!isValidUrl(webLinkContent.url)) {
          errors.push('Please enter a valid URL');
        }
        break;

      default:
        errors.push('Unknown screen type');
    }

    return errors;
  },

  objToBase64JsonString: (obj: any): string => {
    return btoa(JSON.stringify(obj || {}));
  },

  fromBase64ToObj: (base64String: string): any => {
    try {
      return JSON.parse(atob(base64String));
    } catch {
      return null;
    }
  },
};

export interface IFmsApp {
  IsActive?: boolean;
  Name?: string;
  Description?: string;
  LogoUrl?: string;
  AppShortkey?: string;
  HomeScreenPublicId?: string;
  Theme?: IAppTheme;
  Config?: IAppConfig;
  Screens?: IAppScreen[];
  Id?: number;
  PublicId?: string;
  CreatedAtUtcDate?: string;
  LastUpdateAtUtc?: string;
}

export interface IMobileAppInfoes {
  Apps: IFmsApp[];
}

export interface INotificationData {
  PublicId?: string;
  CreatedAtUtc?: string;
  Type: 'Basic';
  Scheduled?: boolean;
  ScheduledForUtc?: string;
  CanceledAtUtc?: string;
  Sent?: boolean;
  Data?: Record<string, any>; // Dictionary for notification body (title, body, priority, etc.)
  NumberSent?: number;
  NumberOfDevices?: number;
  NumberOfErrors?: number;
  FromUserId?: number;
}

export interface IBanner {
  PublicId?: string;
  Id?: number;
  CreatedAtUtc?: string;
  Message: string;
  // Fair-local date/time strings (datetime-local input format). Leave both blank to always display the banner.
  DisplayStartAtUtc?: string;
  DisplayEndAtUtc?: string;
  IsDismissible: boolean;
  AppliesToAllScreens: boolean;
  TargetScreenPublicIds?: string[];
}
